import { createClient, BASE_PATH } from "@stacks/blockchain-api-client";

const REQUEST_TIMEOUT = 12_000;
const MAX_RETRIES = 3;
const RETRY_DELAY = 700;
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
const isTransient = (status: number) => status === 429 || status === 502 || status === 503;

const apiFetch: typeof fetch = async (input, init) => {
  let lastErr: unknown;
  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    try {
      const res = await fetch(input, { ...init, signal: init?.signal ?? AbortSignal.timeout(REQUEST_TIMEOUT) });
      if (isTransient(res.status) && attempt < MAX_RETRIES) {
        await sleep(RETRY_DELAY);
        continue;
      }
      return res;
    } catch (e) {
      lastErr = e;
      if (attempt < MAX_RETRIES) {
        await sleep(RETRY_DELAY);
        continue;
      }
      throw e;
    }
  }
  throw lastErr;
};

export const client = createClient({ baseUrl: BASE_PATH, fetch: apiFetch });

export class AccountNotFound extends Error {
  constructor(public address: string) {
    super("address not resolved: " + address);
    this.name = "AccountNotFound";
  }
}

type RawStxBalance = { balance?: string; nonce?: number; total_fees_sent?: string };
type RawBalances = { stx?: RawStxBalance };

type RawTransfer = { recipient?: string; recipient_address?: string; amount?: string };
type RawContractCall = { contract_id?: string; function_name?: string };
type RawEvent = {
  event_type?: string;
  asset?: {
    asset_event_type?: string;
    sender?: string;
    recipient?: string;
    amount?: string;
    asset_id?: string;
    value?: { repr?: string };
  };
  contract_log?: { contract_id?: string; value?: { repr?: string } };
};

export type RawTx = {
  tx_id?: string;
  tx_type?: string;
  tx_status?: string;
  nonce?: number;
  fee_rate?: string;
  sender_address?: string;
  block_height?: number;
  burn_block_time?: number;
  token_transfer?: RawTransfer;
  contract_call?: RawContractCall;
  smart_contract?: { contract_id?: string };
  event_count?: number;
  events?: RawEvent[];
};

type V2TxItem = { tx?: RawTx } & RawTx;
type V2TxPage = { total?: number; results?: V2TxItem[] };

async function unwrap<T>(p: Promise<{ data?: unknown; response: Response }>): Promise<T> {
  const { data, response } = await p;
  if (!response.ok) {
    if (response.status === 404) throw new NotFound();
    throw new Error("hiro api " + response.status);
  }
  return data as T;
}

class NotFound extends Error {}

export async function fetchBalances(principal: string): Promise<RawBalances> {
  try {
    return await unwrap<RawBalances>(
      client.GET("/extended/v1/address/{principal}/balances", {
        params: { path: { principal } },
      }) as Promise<{ data?: unknown; response: Response }>,
    );
  } catch (e) {
    if (e instanceof NotFound) throw new AccountNotFound(principal);
    throw e;
  }
}

export async function fetchNextNonce(principal: string): Promise<number> {
  try {
    const d = await unwrap<{ possible_next_nonce?: number; last_executed_tx_nonce?: number }>(
      client.GET("/extended/v1/address/{principal}/nonces", {
        params: { path: { principal } },
      }) as Promise<{ data?: unknown; response: Response }>,
    );
    return d.possible_next_nonce ?? (d.last_executed_tx_nonce ?? 0);
  } catch {
    return 0;
  }
}

export async function fetchTransactions(principal: string, limit = 50): Promise<{ total: number; txs: RawTx[] }> {
  const page = await unwrap<V2TxPage>(
    client.GET("/extended/v2/addresses/{address}/transactions", {
      params: { path: { address: principal }, query: { limit } },
    }) as Promise<{ data?: unknown; response: Response }>,
  );
  const txs = (page.results ?? []).map((r) => (r.tx ?? r) as RawTx);
  return { total: page.total ?? txs.length, txs };
}

export async function fetchTx(txId: string): Promise<RawTx> {
  return unwrap<RawTx>(
    client.GET("/extended/v1/tx/{tx_id}", {
      params: { path: { tx_id: txId }, query: { event_limit: 20, event_offset: 0 } },
    }) as Promise<{ data?: unknown; response: Response }>,
  );
}

let tipCache: { at: number; height: number } | null = null;
export async function fetchChainTip(): Promise<number> {
  if (tipCache && Date.now() - tipCache.at < 15_000) return tipCache.height;
  try {
    const res = await apiFetch(BASE_PATH + "/v2/info", {});
    const j = (await res.json()) as { stacks_tip_height?: number };
    const height = j.stacks_tip_height ?? 0;
    tipCache = { at: Date.now(), height };
    return height;
  } catch {
    return tipCache?.height ?? 0;
  }
}
